import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Users, Briefcase, FileText, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTRATS = ["CDI", "CDD", "Stage", "Freelance", "Bénévolat", "Temps partiel"];

const empty = {
  titre: "", description: "", type_contrat: "CDI", lieu: "Kinshasa, RDC",
  salaire: "", profil: "", date_limite: "", actif: true, ordre: 0,
};

export default function AdminJobs() {
  const [offres, setOffres] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [candidatures, setCandidatures] = useState(null); // {offre, list}

  const load = useCallback(() => {
    axios.get(`${API}/admin/offres`, { withCredentials: true }).then((r) => setOffres(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.titre.trim() || !form.description.trim()) {
      toast.error("Titre et description obligatoires");
      return;
    }
    try {
      const payload = {
        ...form,
        salaire: form.salaire || null, profil: form.profil || null,
        date_limite: form.date_limite || null, ordre: Number(form.ordre) || 0,
      };
      if (editId) {
        await axios.put(`${API}/admin/offres/${editId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/offres`, payload, { withCredentials: true });
      }
      toast.success(editId ? "Offre mise à jour" : "Offre publiée");
      setForm(null);
      setEditId(null);
      load();
    } catch {
      toast.error("Erreur d'enregistrement");
    }
  };

  const toggleActif = async (o) => {
    try {
      await axios.put(`${API}/admin/offres/${o.id}`, {
        titre: o.titre, description: o.description, type_contrat: o.type_contrat, lieu: o.lieu,
        salaire: o.salaire, profil: o.profil, date_limite: o.date_limite, actif: !o.actif, ordre: o.ordre,
      }, { withCredentials: true });
      load();
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer l'offre « ${o.titre} » et ses candidatures ?`)) return;
    try {
      await axios.delete(`${API}/admin/offres/${o.id}`, { withCredentials: true });
      toast.success("Offre supprimée");
      load();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const voirCandidatures = async (o) => {
    try {
      const { data } = await axios.get(`${API}/admin/offres/${o.id}/candidatures`, { withCredentials: true });
      setCandidatures({ offre: o, list: data });
    } catch {
      toast.error("Erreur de chargement");
    }
  };

  const startEdit = (o) => {
    setEditId(o.id);
    setForm({
      titre: o.titre, description: o.description, type_contrat: o.type_contrat, lieu: o.lieu,
      salaire: o.salaire || "", profil: o.profil || "", date_limite: o.date_limite || "",
      actif: !!o.actif, ordre: o.ordre || 0,
    });
    setCandidatures(null);
  };

  return (
    <div data-testid="admin-jobs" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl uppercase font-bold">Offres d'emploi ({offres.length})</h2>
          <p className="text-xs text-[#71717A] mt-1">Publiez et gérez les offres affichées sur la page Emplois du site.</p>
        </div>
        <button
          data-testid="job-add-button"
          onClick={() => { setEditId(null); setForm({ ...empty }); setCandidatures(null); }}
          className="flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors"
        >
          <Plus size={14} /> Publier une offre
        </button>
      </div>

      {form && (
        <div className="border border-[#C5A059]/40 bg-[#121214] p-8" data-testid="job-form">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display uppercase font-bold">{editId ? "Modifier l'offre" : "Nouvelle offre"}</h3>
            <button onClick={() => { setForm(null); setEditId(null); }} className="text-[#71717A] hover:text-[#C5A059]" data-testid="job-form-close"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Titre du poste *" data-testid="job-form-titre"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <select value={form.type_contrat} onChange={(e) => setForm({ ...form, type_contrat: e.target.value })} data-testid="job-form-contrat"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]">
              {CONTRATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} placeholder="Lieu" data-testid="job-form-lieu"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input value={form.salaire} onChange={(e) => setForm({ ...form, salaire: e.target.value })} placeholder="Salaire / rémunération (optionnel)" data-testid="job-form-salaire"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input value={form.date_limite} onChange={(e) => setForm({ ...form, date_limite: e.target.value })} placeholder="Date limite de candidature (optionnel)" data-testid="job-form-limite"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input type="number" value={form.ordre} onChange={(e) => setForm({ ...form, ordre: e.target.value })} placeholder="Ordre d'affichage" data-testid="job-form-ordre"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description du poste et missions *" rows={4} data-testid="job-form-description"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] sm:col-span-2" />
            <textarea value={form.profil} onChange={(e) => setForm({ ...form, profil: e.target.value })} placeholder="Profil recherché / exigences (optionnel)" rows={3} data-testid="job-form-profil"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] sm:col-span-2" />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer mt-4">
            <input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} data-testid="job-form-actif" className="accent-[#C5A059]" />
            Publiée sur le site (visible publiquement)
          </label>
          <button onClick={save} data-testid="job-form-save"
            className="mt-6 bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
            {editId ? "Enregistrer" : "Publier"}
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-[#27272A]">
        <table className="w-full text-sm min-w-[800px]" data-testid="jobs-table">
          <thead>
            <tr className="bg-[#121214] text-left text-[10px] uppercase tracking-widest text-[#71717A]">
              <th className="px-4 py-3 font-medium">Poste</th>
              <th className="px-4 py-3 font-medium">Contrat</th>
              <th className="px-4 py-3 font-medium">Lieu</th>
              <th className="px-4 py-3 font-medium">Candidatures</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {offres.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">Aucune offre publiée.</td></tr>
            )}
            {offres.map((o) => (
              <tr key={o.id} data-testid={`job-row-${o.id.slice(0, 8)}`} className="border-t border-[#27272A] hover:bg-[#121214] transition-colors">
                <td className="px-4 py-3 font-medium">{o.titre}</td>
                <td className="px-4 py-3 text-xs text-[#A1A1AA]">{o.type_contrat}</td>
                <td className="px-4 py-3 text-xs text-[#A1A1AA]">{o.lieu}</td>
                <td className="px-4 py-3">
                  <button onClick={() => voirCandidatures(o)} data-testid={`job-candidatures-${o.id.slice(0, 8)}`}
                    className="flex items-center gap-2 text-[#C5A059] font-semibold hover:text-[#E5C07B] transition-colors">
                    <Users size={14} /> {o.candidatures_count}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActif(o)} data-testid={`job-toggle-${o.id.slice(0, 8)}`}
                    className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${o.actif ? "border-emerald-500/60 text-emerald-400" : "border-[#3f3f46] text-[#71717A]"}`}>
                    {o.actif ? "Publiée" : "Retirée"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(o)} data-testid={`job-edit-${o.id.slice(0, 8)}`} className="text-[#A1A1AA] hover:text-[#C5A059] transition-colors" aria-label="Modifier">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(o)} data-testid={`job-delete-${o.id.slice(0, 8)}`} className="text-[#A1A1AA] hover:text-red-400 transition-colors" aria-label="Supprimer">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Candidatures d'une offre */}
      {candidatures && (
        <div className="border border-[#C5A059]/40 bg-[#121214] p-8" data-testid="candidatures-panel">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display uppercase font-bold">
              Candidatures — {candidatures.offre.titre} ({candidatures.list.length})
            </h3>
            <button onClick={() => setCandidatures(null)} className="text-[#71717A] hover:text-[#C5A059]" data-testid="candidatures-close"><X size={18} /></button>
          </div>
          {candidatures.list.length === 0 ? (
            <p className="text-xs text-[#71717A]">Aucune candidature pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {candidatures.list.map((c) => (
                <div key={c.id} data-testid={`candidature-${c.id.slice(0, 8)}`} className="border border-[#27272A] bg-[#0A0A0C] p-5 flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold">{c.nom}</p>
                    <p className="flex items-center gap-2 text-xs text-[#A1A1AA] mt-1"><Mail size={12} /> {c.email}</p>
                    <p className="flex items-center gap-2 text-xs text-[#A1A1AA] mt-1"><Phone size={12} /> {c.telephone}</p>
                    {c.message && <p className="text-xs text-[#71717A] mt-2 max-w-lg leading-relaxed">{c.message}</p>}
                    <p className="text-[10px] text-[#3f3f46] mt-2">{new Date(c.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  {c.cv && (
                    <a href={c.cv} target="_blank" rel="noopener noreferrer" data-testid={`candidature-cv-${c.id.slice(0, 8)}`}
                      className="flex items-center gap-2 border border-[#C5A059]/50 text-[#C5A059] px-4 py-2 text-[10px] uppercase tracking-widest hover:bg-[#C5A059] hover:text-black transition-colors shrink-0">
                      <FileText size={13} /> Voir le CV
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
